import { Component, OnInit, Inject, ElementRef, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Servicio, SubItemTablero } from './interfaces';

@Component({
  selector: 'app-servicios',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './servicios.html',
  styleUrls: ['./servicios.css']
})
export class ServiciosComponent implements OnInit {

  servicioSeleccionadoId: string | null = null;

  private idMap: { [key: number]: string } = {
    1: 'pozos',
    2: 'electromecanicas',
    3: 'electricas',
    4: 'tableros'
  };

  servicios: Servicio[] = [
    {
      id: 'pozos',
      titulo: 'Diseño y Perforación de Pozos para Abastecimiento del Recurso Hídrico Subterráneo',
      tipo: 'lista',
      detalles: [
        'Estudios hidrogeológicos', 'Trámites de licencias', 'Perforación de pozos tubulares profundos.',
        'Mantenimiento integral de pozos tubulares profundos', 'Rehabilitación de pozos tubulares profundos.',
        'Inspección del estado estructural del pozo con cámara de video 4K', 'Equipamiento del sistema de bombeo de pozos tubulares profundos.',
        'Diseño, instalación y mantenimiento de árbol hidráulico del sistema de bombeo de pozo', 'Fabricación en torno de roscas para tuberías de pozos',
        'Fabricación de tuberías bridadas para pozos.', 'Diseño, instalación de red de conducción desde el pozo hasta tanque reservorio',
        'Mantenimiento de equipos de bombeo', 'Rebobinado de motores sumergibles para pozo'
      ]
    },
    {
      id: 'electromecanicas',
      titulo: 'Instalaciones Electromecánicas Automatizadas',
      tipo: 'lista',
      detalles: [
        'Sistema de abastecimiento de agua por bombeo a tanques reservorios.', 'Sistema de presión constante',
        'Sistema contra incendio', 'Sistema de bombeo solar', 'Sistema de drenaje',
        'Sistema de tratamiento de agua', 'Sistema de riego'
      ]
    },
    {
      id: 'electricas',
      titulo: 'Instalaciones Eléctricas y Sistemas de Control',
      tipo: 'lista',
      detalles: [
        'Diseño y ejecución de obras eléctricas, Sistemas de utilización, subsistema de distribución.',
        'Montaje y mantenimiento de Subestaciones del tipo aéreas, biposte y/o monoposte, convencionales, casetas, modulares tipo bloque y compactas.',
        'Tendido de Red aérea y subterránea, MT y BT.',
        'Diseño, Instalación y mantenimiento de Sistemas de Pozo Puestas a Tierra (vertical, horizontal y de malla).'
      ]
    },
    {
      id: 'tableros',
      titulo: 'Fabricación, Instalación y Mantenimiento de Tableros Eléctricos',
      tipo: 'acordeon',
      tableros: [
        {
          titulo: 'Tableros principales',
          descripcion: 'Reciben la alimentación principal y la distribuyen a los circuitos secundarios.',
          items: ['Tableros principales para uso residencial', 'Tableros principales para uso comercial', 'Tableros principales para uso Industrial de los diferentes sectores de explotación y producción.'],
          abierto: true
        },
        {
          titulo: 'Tableros de distribución',
          descripcion: 'Distribuyen la energía eléctrica desde el tablero principal hacia diferentes circuitos secundarios en una instalación.',
          items: ['Tableros de distribución residencial', 'Tableros de distribución comercial', 'Tableros de distribución industrial'],
          abierto: false
        },
        {
          titulo: 'Tableros de fuerza',
          descripcion: 'Diseñados específicamente para alimentar equipos de alta potencia y motores industriales.',
          items: ['Tableros para motores trifásicos', 'Tableros para equipos de soldadura', 'Tableros para maquinaria pesada'],
          abierto: false
        },
        {
          titulo: 'Tableros de arranque de motor',
          descripcion: 'Controlan el arranque, parada y protección de motores eléctricos de diferentes potencias.',
          items: ['Arranque directo (DOL)', 'Arranque estrella-triángulo', 'Arranque suave con variadores de frecuencia'],
          abierto: false
        },
        {
          titulo: 'Tableros de control',
          descripcion: 'Sistemas de control automatizado para procesos industriales y monitoreo de equipos.',
          items: ['Control de procesos automatizados', 'Sistemas de monitoreo remoto', 'Control de iluminación inteligente'],
          abierto: false
        },
        {
          titulo: 'Tableros para bancos de condensadores',
          descripcion: 'Mejoran el factor de potencia y reducen el consumo de energía reactiva en instalaciones industriales.',
          items: ['Compensación automática de reactivos', 'Control inteligente de condensadores', 'Monitoreo de factor de potencia'],
          abierto: false
        },
        {
          titulo: 'Tableros de transferencia automática',
          descripcion: 'Garantizan la continuidad del suministro eléctrico mediante el cambio automático entre fuentes de energía.',
          items: ['Transferencia red-grupo electrógeno', 'Transferencia entre dos redes eléctricas', 'Sistemas UPS integrados'],
          abierto: false
        }
      ]
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private el: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const idParam = params['id'];
      if (idParam) {
        const numericId = Number(idParam);
        this.servicioSeleccionadoId = this.idMap[numericId] || 'pozos';
        // Scroll to the service if an ID was provided in the URL
        this.scrollToService(this.servicioSeleccionadoId);
      } else {
        this.servicioSeleccionadoId = 'pozos';
      }
    });
  }

  private scrollToService(serviceId: string) {
    if (isPlatformBrowser(this.platformId)) {
      // Use a timeout to allow the DOM to update after the selection changes
      setTimeout(() => {
        const element = this.el.nativeElement.querySelector(`[data-servicio="${serviceId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }

  seleccionarServicio(id: string) {
    this.servicioSeleccionadoId = id;
  }

  toggleTablero(subItem: SubItemTablero) {
    if (!subItem.abierto) {
      this.servicios.find(s => s.id === 'tableros')?.tableros?.forEach(t => t.abierto = false);
    }
    subItem.abierto = !subItem.abierto;
  }

  get servicioActual(): Servicio | null {
    if (this.servicioSeleccionadoId === null) {
      return null;
    }
    return this.servicios.find(s => s.id === this.servicioSeleccionadoId) || null;
  }
}
